import {
  type FamilyDebtRiskInput,
  FamilyDebtRiskInputSchema,
  type FamilyDebtRiskOutput,
  type FamilyPaymentsRuleSet,
  type FtbEligibilityGapInput,
  FtbEligibilityGapInputSchema,
  type FtbEligibilityGapOutput,
  type FtbEntitlementInput,
  FtbEntitlementInputSchema,
  type FtbEntitlementOutput,
  type FtbPartAResult,
  type FtbPartBResult,
  type FtbSupplementResult,
} from "./types.js";

/** Round money to 2 decimal places. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Per-fortnight FTB Part A MAX rate for one child by age band. */
function childMaxRate(age: number, rules: FamilyPaymentsRuleSet): number {
  // 0-12 vs 13-19. Services Australia FTB Part A rates: the higher band covers
  // 13-15 and 16-19 in approved full-time secondary study.
  return age <= 12
    ? rules.partAMaxRates.age0to12
    : rules.partAMaxRates.age13to19;
}

/**
 * FTB Part A under the two-stage Method-1 income test.
 *
 * Sum the per-child max rates and the per-child base rates. Then:
 *   1. Above the lower free area, reduce the MAX rate by 20c/$1, but never below
 *      the base rate (the base rate is the floor of the first taper).
 *   2. The base rate keeps paying up to the higher free area.
 *   3. Above the higher free area, reduce the BASE rate by 30c/$1 to nil.
 *
 * The income excess produces ONE family-level reduction applied to the summed
 * family rate (not a per-child taper), per Method 1.
 */
function calcPartA(
  input: FtbEntitlementInput,
  rules: FamilyPaymentsRuleSet,
): FtbPartAResult {
  const t = rules.partAIncomeTest;
  const ati = input.familyAti;

  const maxRatePerFortnight = round2(
    input.children.reduce((sum, c) => sum + childMaxRate(c.age, rules), 0),
  );
  const baseRatePerFortnight = round2(input.children.length * t.baseRatePerChild);

  let ratePerFortnight: number;
  let taperStage: FtbPartAResult["taperStage"];

  if (maxRatePerFortnight === 0) {
    // No children claimed for Part A.
    ratePerFortnight = 0;
    taperStage = "nil";
  } else if (ati <= t.lowerFreeArea) {
    ratePerFortnight = maxRatePerFortnight;
    taperStage = "max";
  } else if (ati <= t.higherFreeArea) {
    // First taper: 20c/$1 off the MAX rate, floored at the base rate. Method 1
    // reduces an ANNUAL amount; we convert the per-fortnight rates to annual,
    // apply the per-$1 reduction, floor at base, then back to per fortnight.
    const annualMax = maxRatePerFortnight * rules.fortnightsPerYear;
    const annualBase = baseRatePerFortnight * rules.fortnightsPerYear;
    const reduction = (ati - t.lowerFreeArea) * t.lowerTaperPerDollar;
    const reducedAnnual = Math.max(annualBase, annualMax - reduction);
    ratePerFortnight = round2(reducedAnnual / rules.fortnightsPerYear);
    taperStage =
      reducedAnnual <= annualBase ? "base" : "lower-taper";
  } else {
    // Second taper: base rate reduced 30c/$1 to nil above the higher free area.
    const annualBase = baseRatePerFortnight * rules.fortnightsPerYear;
    const reduction = (ati - t.higherFreeArea) * t.higherTaperPerDollar;
    const reducedAnnual = Math.max(0, annualBase - reduction);
    ratePerFortnight = round2(reducedAnnual / rules.fortnightsPerYear);
    taperStage = reducedAnnual <= 0 ? "nil" : "higher-taper";
  }

  return {
    maxRatePerFortnight,
    baseRatePerFortnight,
    ratePerFortnight,
    annualRate: round2(ratePerFortnight * rules.fortnightsPerYear),
    taperStage,
  };
}

/**
 * FTB Part B: a primary-earner CLIFF then a secondary-earner taper.
 *   - If the primary earner's ATI exceeds the limit ($120,007) → no Part B.
 *   - Otherwise Part B max (by youngest-child age band), reduced 20c/$1 above the
 *     secondary-earner free area ($6,935). Single parents have NO secondary test.
 */
function calcPartB(
  input: FtbEntitlementInput,
  rules: FamilyPaymentsRuleSet,
): FtbPartBResult {
  const t = rules.partBIncomeTest;

  if (!input.claimPartB) {
    return { payable: false, ratePerFortnight: 0, annualRate: 0, nilReason: null };
  }

  // Primary-earner cliff.
  if (input.primaryEarnerAti > t.primaryEarnerLimit) {
    return {
      payable: false,
      ratePerFortnight: 0,
      annualRate: 0,
      nilReason: "over-primary-limit",
    };
  }

  const youngest = input.youngestChildAge ?? 0;
  const maxRate =
    youngest < 5 ? t.maxRateYoungestUnder5 : t.maxRateYoungest5to18;

  // Single parents: no secondary-earner test — they receive the max rate
  // (subject only to the primary limit, already passed).
  if (input.singleParent) {
    return {
      payable: true,
      ratePerFortnight: maxRate,
      annualRate: round2(maxRate * rules.fortnightsPerYear),
      nilReason: null,
    };
  }

  // Couples: secondary-earner taper. Reduce 20c/$1 above the free area, on the
  // annual amount, floored at 0.
  const annualMax = maxRate * rules.fortnightsPerYear;
  const excess = Math.max(0, input.secondaryEarnerAti - t.secondaryEarnerFreeArea);
  const reducedAnnual = Math.max(0, annualMax - excess * t.secondaryTaperPerDollar);

  if (reducedAnnual <= 0) {
    return {
      payable: false,
      ratePerFortnight: 0,
      annualRate: 0,
      nilReason: "secondary-taper-out",
    };
  }

  return {
    payable: true,
    ratePerFortnight: round2(reducedAnnual / rules.fortnightsPerYear),
    annualRate: round2(reducedAnnual),
    nilReason: null,
  };
}

/**
 * End-of-year supplements. Both are gated by the $80,000 ATI cap as an
 * all-or-nothing CLIFF: above it, both are nil with NO taper. The supplement
 * does NOT persist above the cap.
 */
function calcSupplements(
  input: FtbEntitlementInput,
  partB: FtbPartBResult,
  rules: FamilyPaymentsRuleSet,
): FtbSupplementResult {
  const s = rules.supplements;
  const overCap = input.familyAti > s.supplementIncomeCap;

  if (overCap) {
    return { partA: 0, partB: 0, overSupplementCap: true };
  }

  const partA = round2(input.children.length * s.partAPerChild);
  // Part B supplement only if Part B is payable.
  const partBSupp = partB.payable ? s.partBPerFamily : 0;

  return { partA, partB: partBSupp, overSupplementCap: false };
}

/**
 * Estimate FTB Part A + Part B + supplements for a year. Pure. Validates input
 * at the boundary. The total is an INDICATIVE ESTIMATE — Services Australia
 * finalises the actual entitlement at end-of-year balancing.
 */
export function ftbEntitlement(
  rawInput: FtbEntitlementInput,
  rules: FamilyPaymentsRuleSet,
): FtbEntitlementOutput {
  const input = FtbEntitlementInputSchema.parse(rawInput);

  const partA = calcPartA(input, rules);
  const partB = calcPartB(input, rules);
  const supplement = calcSupplements(input, partB, rules);

  const estimatedAnnualTotal = round2(
    partA.annualRate +
      partB.annualRate +
      supplement.partA +
      supplement.partB,
  );

  return { partA, partB, supplement, estimatedAnnualTotal };
}

/**
 * The income at which Part A's base rate tapers to nil, for a given child count.
 * Above the higher free area the base rate reduces 30c/$1; this returns the ATI
 * where the annualised family base rate hits zero.
 */
export function baseRateCutoff(
  childCount: number,
  rules: FamilyPaymentsRuleSet,
): number {
  const t = rules.partAIncomeTest;
  const annualBase = childCount * t.baseRatePerChild * rules.fortnightsPerYear;
  return round2(t.higherFreeArea + annualBase / t.higherTaperPerDollar);
}

/**
 * Eligibility-gap check: "you may be eligible past where you think." The base
 * rate of Part A keeps paying above the max-rate (lower free area) cutoff —
 * many families self-select out at the lower threshold. Information only: this
 * surface frames the result as "easy to check", NEVER "you're missing money"
 * (the leakage stat is UNVERIFIED — see family-payments figure ledgers).
 *
 * The supplement is capped at $80,000 ATI; this output flags when it is
 * unavailable so copy NEVER implies the supplement persists above the cap.
 */
export function ftbEligibilityGap(
  rawInput: FtbEligibilityGapInput,
  rules: FamilyPaymentsRuleSet,
): FtbEligibilityGapOutput {
  const input = FtbEligibilityGapInputSchema.parse(rawInput);
  const t = rules.partAIncomeTest;
  const cutoff = baseRateCutoff(input.childCount, rules);

  return {
    aboveMaxRateCutoff: input.familyAti > t.lowerFreeArea,
    baseRateMayApply: input.familyAti < cutoff,
    supplementUnavailable: input.familyAti > rules.supplements.supplementIncomeCap,
    maxRateCutoff: t.lowerFreeArea,
    baseRateCutoff: cutoff,
  };
}

/**
 * Cross-payment reconciliation debt-risk. ONE family income estimate drives BOTH
 * FTB and CCS; an UNDER-estimate creates correlated debts on both. CCS has a 5%
 * (adjustable) withholding buffer that absorbs part of its debt; FTB has NO
 * equivalent and is unbuffered.
 *
 * Framing (binding, panel ship-condition): the figure is "AT LEAST this much,
 * possibly more" — a floor, never a falsely reassuring point estimate. FTB and
 * CCS exposures are surfaced SEPARATELY so the combination is transparent.
 * Services Australia finalises the actual figure at balancing.
 *
 * NOTE: the Education Legislation Amendment (Integrity and Other Measures) Act
 * 2025 date-of-effect change applies to CCS ONLY, never FTB — it is encoded (if
 * at all) in the au13 CCS engine's date-of-effect logic, NOT here.
 */
export function familyDebtRisk(
  rawInput: FamilyDebtRiskInput,
  rules: FamilyPaymentsRuleSet,
): FamilyDebtRiskOutput {
  const input = FamilyDebtRiskInputSchema.parse(rawInput);

  const withholdingRate =
    input.ccsWithholdingRate ?? rules.ccsWithholding.defaultRate;

  const shortfall = input.projectedActualAti - input.estimatedFamilyAti;
  const underEstimated = shortfall > 0;

  if (!underEstimated) {
    return {
      underEstimated: false,
      estimateShortfall: 0,
      ftbDebtExposure: 0,
      ccsDebtExposure: 0,
      ccsBufferCredit: 0,
      combinedDebtExposure: 0,
      ccsWithholdingRate: withholdingRate,
    };
  }

  // Annualise the paid-per-fortnight figures into the year's instalments. The
  // exposure floor is the full year of payments that the under-estimate inflated
  // — a conservative "at least this much": we surface what is at stake, and
  // Services Australia computes the exact delta at balancing.
  const ftbAnnualPaid = input.ftbPaidPerFortnight * rules.fortnightsPerYear;
  const ccsAnnualPaid = input.ccsPaidPerFortnight * rules.fortnightsPerYear;

  // FTB is UNBUFFERED — its exposure is carried in full.
  const ftbDebtExposure = round2(ftbAnnualPaid);

  // CCS — the withholding buffer is set aside against any overpayment, so it
  // credits against the CCS exposure first.
  const ccsBufferCredit = round2(ccsAnnualPaid * withholdingRate);
  const ccsDebtExposure = round2(Math.max(0, ccsAnnualPaid - ccsBufferCredit));

  const combinedDebtExposure = round2(ftbDebtExposure + ccsDebtExposure);

  return {
    underEstimated: true,
    estimateShortfall: round2(shortfall),
    ftbDebtExposure,
    ccsDebtExposure,
    ccsBufferCredit,
    combinedDebtExposure,
    ccsWithholdingRate: withholdingRate,
  };
}
